import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, Search, Phone, MapPin, RefreshCw, Users, ChevronRight, X, ArrowUpDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import api from '../services/api';
import { useScrollRestore } from '../hooks/useScrollRestore';

const fmt = (d) => {
  if (!d) return '';
  try {
    return new Date(d + (d.includes('Z') || d.includes('+') ? '' : 'Z'))
      .toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return ''; }
};

const convRate = (c) => {
  const decided = (c.won_leads || 0) + (c.lost_leads || 0);
  return decided > 0 ? (c.won_leads || 0) / decided : -1;
};

const SORT_OPTIONS = [
  { value: 'last_activity', label: 'Last Active' },
  { value: 'most_sessions', label: 'Most Sessions' },
  { value: 'most_visits',   label: 'Most Visits'   },
  { value: 'conversion',    label: 'Conversion Rate' },
  { value: 'name',          label: 'Name A–Z'       },
];

const FILTER_OPTIONS = [
  { value: 'all',         label: 'All Clients'      },
  { value: 'open',        label: 'Open Leads'       },
  { value: 'won',         label: 'Has Won'          },
  { value: 'lost',        label: 'Has Lost'         },
  { value: 'multi_visit', label: 'Return Visitors'  },
];

const Clients = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [allClients, setAllClients] = useState([]);
  const [query,      setQuery]      = useState(searchParams.get('q') || '');
  const [sortBy,     setSortBy]     = useState(searchParams.get('sort') || 'last_activity');
  const [filterBy,   setFilterBy]   = useState(searchParams.get('filter') || 'all');
  const [loading,    setLoading]    = useState(true);
  const debounceRef                 = useRef(null);
  const [debouncedQ, setDebouncedQ] = useState(searchParams.get('q') || '');
  const listRef                     = useScrollRestore('clients-list');
  const isMounted                   = useRef(false);

  useEffect(() => {
    api.get('/crm/clients').then(r => setAllClients(r.data)).catch(console.error).finally(() => setLoading(false));
  }, []);

  // Sync filters → URL silently (bypasses React Router history so back nav is unaffected)
  useEffect(() => {
    if (!isMounted.current) { isMounted.current = true; return; }
    const url = new URL(window.location.href);
    if (query) url.searchParams.set('q', query); else url.searchParams.delete('q');
    if (sortBy && sortBy !== 'last_activity') url.searchParams.set('sort', sortBy); else url.searchParams.delete('sort');
    if (filterBy && filterBy !== 'all') url.searchParams.set('filter', filterBy); else url.searchParams.delete('filter');
    window.history.replaceState(window.history.state, '', url.toString());
  }, [query, sortBy, filterBy]);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedQ(query.trim().toLowerCase()), 200);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  const display = useMemo(() => {
    let list = [...allClients];

    // Search
    if (debouncedQ) {
      list = list.filter(c =>
        (c.client_name || '').toLowerCase().includes(debouncedQ) ||
        (c.client_phone || '').includes(debouncedQ)
      );
    }

    // Status filter
    if (filterBy === 'open')        list = list.filter(c => c.open_sessions > 0);
    if (filterBy === 'won')         list = list.filter(c => (c.won_leads || 0) > 0);
    if (filterBy === 'lost')        list = list.filter(c => (c.lost_leads || 0) > 0);
    if (filterBy === 'multi_visit') list = list.filter(c => (c.total_visits || 1) > 1);

    // Sort
    if (sortBy === 'most_sessions') list.sort((a, b) => b.total_sessions - a.total_sessions);
    if (sortBy === 'most_visits')   list.sort((a, b) => (b.total_visits || 1) - (a.total_visits || 1));
    if (sortBy === 'name')          list.sort((a, b) => (a.client_name || '').localeCompare(b.client_name || ''));
    if (sortBy === 'conversion')    list.sort((a, b) => convRate(b) - convRate(a));
    // 'last_activity' keeps API order (already sorted desc by last_activity)

    return list;
  }, [allClients, debouncedQ, sortBy, filterBy]);

  const activeFilter = filterBy !== 'all';

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-[#075E54] text-white px-4 py-3 shadow-md flex-shrink-0">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="text-white hover:bg-emerald-700 p-2 flex-shrink-0"
            data-testid="clients-back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold" data-testid="clients-page-title">Clients</h1>
            <p className="text-xs text-emerald-200">
              {loading ? 'Loading...' : `${display.length} of ${allClients.length} clients`}
            </p>
          </div>
        </div>
      </div>

      {/* Search + Controls */}
      <div className="px-4 py-3 bg-white border-b flex-shrink-0 space-y-2">
        {/* Search */}
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            autoFocus
            type="text"
            inputMode="search"
            placeholder="Search by name or phone..."
            value={query}
            onChange={e => setQuery(e.target.value)}
            className="w-full pl-9 pr-9 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
            data-testid="client-search-input"
          />
          {query && (
            <button
              onClick={() => setQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              data-testid="clear-search-btn"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Sort + Filter row */}
        <div className="flex gap-2">
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="h-8 text-xs flex-1" data-testid="clients-sort-select">
              <ArrowUpDown className="w-3 h-3 mr-1 text-gray-400 flex-shrink-0" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SORT_OPTIONS.map(o => (
                <SelectItem key={o.value} value={o.value} className="text-xs">{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={filterBy} onValueChange={setFilterBy}>
            <SelectTrigger
              className={`h-8 text-xs flex-1 ${activeFilter ? 'border-emerald-500 text-emerald-700 bg-emerald-50' : ''}`}
              data-testid="clients-filter-select"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FILTER_OPTIONS.map(o => (
                <SelectItem key={o.value} value={o.value} className="text-xs">{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {(activeFilter || query) && (
            <button
              onClick={() => { setFilterBy('all'); setQuery(''); setSortBy('last_activity'); }}
              className="h-8 px-2 text-xs text-red-500 hover:text-red-700 border border-red-200 rounded-md hover:bg-red-50 transition-colors whitespace-nowrap"
              data-testid="clear-all-filters-btn"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* List */}
      <div ref={listRef} className="flex-1 overflow-y-auto" data-testid="clients-list">
        {loading ? (
          <div className="flex flex-col items-center justify-center h-full gap-3">
            <RefreshCw className="w-8 h-8 animate-spin text-emerald-600" />
            <p className="text-sm text-gray-400">Loading clients...</p>
          </div>
        ) : display.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full gap-3 text-gray-400">
            <Users className="w-16 h-16 opacity-30" />
            <p className="text-sm font-medium">No clients match</p>
            <button
              onClick={() => { setFilterBy('all'); setQuery(''); setSortBy('last_activity'); }}
              className="text-xs text-emerald-600 hover:underline"
            >
              Clear all filters
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {display.map((client) => {
              const rate = convRate(client);
              return (
                <button
                  key={client.client_phone}
                  onClick={() => navigate(`/client-timeline/${encodeURIComponent(client.client_phone)}`)}
                  className="w-full px-4 py-3.5 bg-white hover:bg-emerald-50 transition-colors flex items-center gap-3 text-left"
                  data-testid={`client-row-${client.client_phone}`}
                >
                  {/* Avatar */}
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                    <span className="text-emerald-700 font-bold text-sm">
                      {(client.client_name || '?').charAt(0).toUpperCase()}
                    </span>
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 truncate text-sm">{client.client_name}</p>
                    <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                      <Phone className="w-3 h-3" />{client.client_phone}
                    </p>
                  </div>

                  {/* Stats */}
                  <div className="flex-shrink-0 text-right mr-1">
                    <div className="flex items-center justify-end gap-2 text-xs">
                      {/* Won / Lost badges */}
                      {(client.won_leads > 0 || client.lost_leads > 0) && (
                        <div className="flex items-center gap-1">
                          {client.won_leads > 0 && (
                            <span className="px-1.5 py-0.5 bg-green-100 text-green-700 rounded font-semibold text-[10px]" title="Won leads">
                              {client.won_leads}W
                            </span>
                          )}
                          {client.lost_leads > 0 && (
                            <span className="px-1.5 py-0.5 bg-red-100 text-red-700 rounded font-semibold text-[10px]" title="Lost leads">
                              {client.lost_leads}L
                            </span>
                          )}
                        </div>
                      )}
                      <span className="text-gray-500">
                        <span className="font-semibold text-gray-700">{client.total_sessions}</span>
                        {' '}session{client.total_sessions !== 1 ? 's' : ''}
                      </span>
                      <span className="flex items-center gap-1 text-emerald-600" title="Total visits">
                        <MapPin className="w-3 h-3" />
                        <span className="font-semibold">{client.total_visits}</span>
                      </span>
                    </div>
                    <div className="flex items-center justify-end gap-2 mt-1">
                      {client.open_sessions > 0 && (
                        <span className="text-[10px] px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded-full font-semibold">
                          {client.open_sessions} open
                        </span>
                      )}
                      {/* Conversion rate — only if there are decided leads */}
                      {rate >= 0 && (
                        <span
                          className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${rate >= 0.5 ? 'bg-green-50 text-green-600' : 'bg-orange-50 text-orange-600'}`}
                          title="Conversion rate"
                          data-testid={`conv-rate-${client.client_phone}`}
                        >
                          {Math.round(rate * 100)}%
                        </span>
                      )}
                    </div>
                    <p className="text-[10px] text-gray-400 mt-0.5">{fmt(client.last_activity)}</p>
                  </div>

                  <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0" />
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Clients;
